/** Shared per-lift badge letters/variants (per-lift cards + Lift Balance rows). */
export const LIFT_CARD_BADGE_META = Object.freeze({
  squat: { badgeLetter: "S", badgeVariant: "squat" },
  bench: { badgeLetter: "B", badgeVariant: "bench" },
  deadlift: { badgeLetter: "D", badgeVariant: "deadlift" },
  clean: { badgeLetter: "C", badgeVariant: "clean" },
  jerk: { badgeLetter: "J", badgeVariant: "jerk" },
  snatch: { badgeLetter: "Sn", badgeVariant: "snatch" },
  cleanJerk: { badgeLetter: "CJ", badgeVariant: "cleanJerk" },
  overhead: { badgeLetter: "OH", badgeVariant: "overhead" },
  muscleUp: { badgeLetter: "MU", badgeVariant: "muscleUp" },
  weightedDip: { badgeLetter: "WD", badgeVariant: "weightedDip" },
  weightedPullUp: { badgeLetter: "WP", badgeVariant: "weightedPullUp" },
});

/** Function to get the badge meta for a given badge key.
* @param {string} badgeKey */
export function liftBadgeMetaForKey(badgeKey) {
  const meta = LIFT_CARD_BADGE_META[badgeKey];
  if (meta) {
    return meta;
  }
  return {
    badgeLetter: badgeKey.charAt(0).toUpperCase(),
    badgeVariant: badgeKey,
  };
}

/** @typedef {import("./insightsLensCompVolume").InsightsPerLiftLens} InsightsPerLiftLens */

/** Lenses that render the Lift Balance footer section (Strongman excluded in v1). */
export const LIFT_BALANCE_LENS_IDS = Object.freeze([
  "powerlifting",
  "olympic_weightlifting",
  "streetlifting",
]);

/**
 * Contested-lift buckets per lens. Badge copy comes from `LIFT_CARD_BADGE_META`.
 * @type {Readonly<Record<string, ReadonlyArray<{
 *   rowId: string,
 *   sourceLiftIds: string[],
 *   nameKey?: string,
 *   badgeKey?: string,
 * }>>>}
 */
export const LIFT_BALANCE_ROW_CONFIG = Object.freeze({
  powerlifting: Object.freeze([
    { rowId: "squat", sourceLiftIds: ["squat"] },
    { rowId: "bench", sourceLiftIds: ["bench"] },
    { rowId: "deadlift", sourceLiftIds: ["deadlift"] },
  ]),
  olympic_weightlifting: Object.freeze([
    { rowId: "snatch", sourceLiftIds: ["snatch"] },
    {
      rowId: "cleanJerk",
      sourceLiftIds: ["clean", "jerk"],
      nameKey: "cleanJerk",
      badgeKey: "cleanJerk",
    },
  ]),
  streetlifting: Object.freeze([
    { rowId: "weightedPullUp", sourceLiftIds: ["weightedPullUp"] },
    { rowId: "weightedDip", sourceLiftIds: ["weightedDip"] },
    { rowId: "muscleUp", sourceLiftIds: ["muscleUp"] },
  ]),
});

/** Function to compute the total number of sets for a given list of lift ids.
 * @param {Map<string, { sets?: number }>|undefined|null} perLiftScalars
 * @param {string[]} sourceLiftIds
 * @returns {number}
 */
function contestedSetsForSourceLiftIds(perLiftScalars, sourceLiftIds) {
  let total = 0;
  for (const liftId of sourceLiftIds) {
    total += perLiftScalars?.get(liftId)?.sets ?? 0;
  }
  return total;
}

/**
 * Round row percentages and nudge the largest raw bucket when sum is 99 or 101.
 * @param {Record<string, number>} rawByRowId
 * @returns {Record<string, number>}
 */
export function roundLiftBalancePercentages(rawByRowId) {
  const ids = Object.keys(rawByRowId);
  if (ids.length === 0) {
    return {};
  }

  /** @type {Record<string, number>} */
  const rounded = {};
  for (const id of ids) {
    rounded[id] = Math.round(rawByRowId[id]);
  }

  const sum = ids.reduce((acc, id) => acc + rounded[id], 0);
  if (sum !== 99 && sum !== 101) {
    return rounded;
  }

  let maxId = ids[0];
  let maxRaw = rawByRowId[maxId] ?? 0;
  for (const id of ids) {
    const raw = rawByRowId[id] ?? 0;
    if (raw > maxRaw) {
      maxRaw = raw;
      maxId = id;
    }
  }
  rounded[maxId] += sum === 99 ? 1 : -1;
  return rounded;
}

/** Function to check if a lens supports lift balance.
 * @param {InsightsPerLiftLens|string} lensId
 * @returns {boolean}
 */
export function lensSupportsLiftBalance(lensId) {
  return LIFT_BALANCE_LENS_IDS.includes(lensId);
}

/**
 * Working-set distribution across contested lifts for one lens/week.
 *
 * @param {InsightsPerLiftLens|string} lensId
 * @param {Map<string, { sets?: number }>|undefined|null} perLiftScalars
 * @returns {{ rows: object[] }|null}
 */
export function computeLiftBalanceForLens(lensId, perLiftScalars) {
  if (!lensSupportsLiftBalance(lensId)) {
    return null;
  }

  const rowConfig = LIFT_BALANCE_ROW_CONFIG[lensId];
  const setCounts = rowConfig.map((row) => ({
    row,
    sets: contestedSetsForSourceLiftIds(perLiftScalars, row.sourceLiftIds),
  }));
  const totalContestedSets = setCounts.reduce((sum, entry) => sum + entry.sets, 0);
  if (totalContestedSets <= 0) {
    return null;
  }

  /** @type {Record<string, number>} */
  const rawByRowId = {};
  for (const { row, sets } of setCounts) {
    rawByRowId[row.rowId] = (sets / totalContestedSets) * 100;
  }
  const pctByRowId = roundLiftBalancePercentages(rawByRowId);

  return {
    rows: setCounts.map(({ row, sets }) => {
      const pct = pctByRowId[row.rowId] ?? 0;
      const badge = liftBadgeMetaForKey(row.badgeKey ?? row.rowId);
      return {
        rowId: row.rowId,
        nameKey: row.nameKey ?? row.rowId,
        badgeLetter: badge.badgeLetter,
        badgeVariant: badge.badgeVariant,
        sets,
        pct,
        pctDisplay: `${pct}%`,
      };
    }),
  };
}
